import { useCallback, useEffect, useState } from 'react';
import { getRequest, type RequestDetail } from '../../lib/api';
import { useBackButton } from '../../hooks/useBackButton';

function formatTime(iso: string): string {
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const hours = Math.floor(diffMs / (60 * 60 * 1000));
    const days = Math.floor(diffMs / (24 * 60 * 60 * 1000));
    if (hours < 1) return 'только что';
    if (hours < 24) return `${hours} ч назад`;
    if (days === 1) return 'вчера';
    if (days < 7) return `${days} дн. назад`;
    return 'неделю назад';
  } catch {
    return '';
  }
}

function typeBadgeClass(type: string): string {
  const t = type.toLowerCase();
  if (t === 'development' || t.includes('разработ')) return 'detail-badge detail-badge--dev';
  if (t === 'partner' || t.includes('партн')) return 'detail-badge detail-badge--partner';
  if (t === 'investor' || t === 'invest' || t.includes('инвест')) return 'detail-badge detail-badge--invest';
  return 'detail-badge';
}

interface RequestDetailViewProps {
  requestId: string;
  onBack: () => void;
  onRespond: (id: string) => void;
  respondedIds: Set<string>;
  onOpenAuthorProfile?: (authorId: number) => void;
  hideRespondButton?: boolean;
  ownerActionLabel?: string | null;
  onOwnerAction?: (id: string) => void;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  actionDisabled?: boolean;
}

export function RequestDetailView({
  requestId,
  onBack,
  onRespond,
  respondedIds,
  onOpenAuthorProfile,
  hideRespondButton = false,
  ownerActionLabel = null,
  onOwnerAction,
  onEdit,
  onDelete,
  actionDisabled = false,
}: RequestDetailViewProps) {
  const [request, setRequest] = useState<RequestDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getRequest(requestId).then((data) => {
      if (!cancelled) {
        setRequest(data ?? null);
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [requestId]);

  const responded = request ? (request.i_responded || respondedIds.has(request.id)) : false;

  const openTgContact = useCallback((username: string) => {
    const url = `https://t.me/${username.replace(/^@/, '')}`;
    const tg = (window as unknown as { Telegram?: { WebApp?: { openTelegramLink?: (u: string) => void } } }).Telegram?.WebApp;
    if (tg?.openTelegramLink) tg.openTelegramLink(url);
    else window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  const openAuthorProfile = useCallback(() => {
    const authorId = request?.author?.id;
    if (!authorId || !onOpenAuthorProfile) return;
    onOpenAuthorProfile(authorId);
  }, [onOpenAuthorProfile, request?.author?.id]);

  const handleRespondClick = useCallback(() => {
    if (!request || responded) return;
    onRespond(request.id);
  }, [request, responded, onRespond]);

  const handleOwnerActionClick = useCallback(() => {
    if (!request || !onOwnerAction || actionDisabled) return;
    onOwnerAction(request.id);
  }, [request, onOwnerAction, actionDisabled]);

  const handleEditClick = useCallback(() => {
    if (!request || !onEdit || actionDisabled) return;
    onEdit(request.id);
  }, [request, onEdit, actionDisabled]);

  const handleDeleteClick = useCallback(() => {
    if (!request || !onDelete || actionDisabled) return;
    onDelete(request.id);
  }, [request, onDelete, actionDisabled]);

  useBackButton(onBack, true);

  if (loading) {
    return (
      <div className={`requests-screen request-detail-screen${hideRespondButton ? ' request-detail-screen--readonly' : ''}`}>
        <header className="detail-header-bar">
          <div className="detail-header-spacer" aria-hidden />
          <h1 className="detail-header-title">Заявка</h1>
        </header>
        <div className="requests-loading">Загрузка…</div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className={`requests-screen request-detail-screen${hideRespondButton ? ' request-detail-screen--readonly' : ''}`}>
        <header className="detail-header-bar">
          <div className="detail-header-spacer" aria-hidden />
          <h1 className="detail-header-title">Заявка</h1>
        </header>
        <p className="showcase-empty__title">Заявка не найдена</p>
      </div>
    );
  }

  const taskText = request.task?.trim() || request.description?.trim();
  const moderationNote =
    request.status === 'rejected'
      ? request.rejected_reason
      : request.status === 'needs_fix'
        ? request.needs_fix_comment
        : null;

  return (
    <div className={`requests-screen request-detail-screen${hideRespondButton ? ' request-detail-screen--readonly' : ''}`}>
      <header className="detail-header-bar">
        <div className="detail-header-spacer" aria-hidden />
        <div className="detail-header-titles">
          <h1 className="detail-header-title">Заявка</h1>
          <p className="detail-header-subtitle">{request.title}</p>
        </div>
      </header>

      <div className="detail-scroll">
        <section className="detail-hero detail-hero--row">
          <span className="detail-hero-emoji detail-hero-emoji--square request-detail-avatar" aria-hidden>
            {request.author?.username?.[0]?.toUpperCase() ?? '?'}
          </span>
          <div className="detail-hero-body">
            <h2 className="detail-hero-title">{request.title}</h2>
            <div className="detail-hero-author">
              {request.author?.id && onOpenAuthorProfile ? (
                <button
                  type="button"
                  className="detail-hero-author-name detail-hero-author-name--clickable"
                  onClick={openAuthorProfile}
                >
                  @{request.author?.username ?? 'автор'} · {formatTime(request.published_at)}
                </button>
              ) : (
                <span className="detail-hero-author-name">
                  @{request.author?.username ?? 'автор'} · {formatTime(request.published_at)}
                </span>
              )}
            </div>
            {request.urgent && (
              <span className="request-detail-urgent">Срочно</span>
            )}
            <div className="detail-hero-badges">
              <span className={typeBadgeClass(request.type)}>{request.type_label}</span>
              {request.budget && <span className="detail-badge detail-badge--budget">Бюджет</span>}
              {request.deadline && (
                <span className="detail-badge detail-badge--deadline">Срок: {request.deadline}</span>
              )}
            </div>
          </div>
        </section>

        <section className="detail-metrics">
          <h3 className="detail-metrics-label">ПАРАМЕТРЫ</h3>
          <div className="detail-metrics-grid">
            <div className="detail-metric">
              <div className="detail-metric-content">
                <span className="detail-metric-name">Бюджет</span>
                <span className="detail-metric-value detail-metric-value--green">
                  {request.budget ?? '—'}
                </span>
              </div>
            </div>
            <div className="detail-metric">
              <div className="detail-metric-content">
                <span className="detail-metric-name">Срок</span>
                <span className="detail-metric-value detail-metric-value--purple">
                  {request.deadline ?? '—'}
                </span>
              </div>
            </div>
            <div className="detail-metric">
              <div className="detail-metric-content">
                <span className="detail-metric-name">Формат</span>
                <span className="detail-metric-value detail-metric-value--muted">
                  {request.format ?? 'Не указано'}
                </span>
              </div>
            </div>
            <div className="detail-metric">
              <div className="detail-metric-content">
                <span className="detail-metric-name">Просмотры</span>
                <span className="detail-metric-value detail-metric-value--orange">
                  {request.views_count ?? request.responses_count ?? 0}
                </span>
              </div>
            </div>
          </div>
        </section>

        {taskText && (
          <section className="detail-block">
            <h3 className="detail-block-label">ЗАДАЧА</h3>
            <p className="detail-block-text">{taskText}</p>
          </section>
        )}

        {request.tools && request.tools.length > 0 && (
          <section className="detail-block">
            <h3 className="detail-block-label">СТЕК ИНСТРУМЕНТОВ</h3>
            <div className="detail-stack">
              {request.tools.map((tool) => (
                <span key={tool} className="detail-stack-badge">
                  {tool}
                </span>
              ))}
            </div>
          </section>
        )}

        {moderationNote && (
          <section className="detail-block">
            <h3 className="detail-block-label">КОММЕНТАРИЙ МОДЕРАЦИИ</h3>
            <p className="request-detail-moderation-note">{moderationNote}</p>
          </section>
        )}

        {request.responses && request.responses.length > 0 && (
          <section className="detail-block">
            <h3 className="detail-block-label">ОТКЛИКИ {request.responses.length}</h3>
            <div className="request-detail-responses">
              {request.responses.map((r) => (
                <div key={r.id} className="request-response-item">
                  <span className="request-response-avatar">{r.author?.username?.[0]?.toUpperCase() ?? '?'}</span>
                  <div className="request-response-body">
                    <span className="request-response-username">@{r.author?.username ?? ''}</span>
                    {r.summary && <span className="request-response-summary">{r.summary}</span>}
                    {r.rating != null && <span className="request-response-rating">★ {r.rating}</span>}
                  </div>
                  <button
                    type="button"
                    className="request-response-write"
                    onClick={() => r.author?.username && openTgContact(r.author.username)}
                    disabled={!r.author?.username}
                  >
                    Написать →
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}

        <div className="detail-spacer" aria-hidden />
      </div>

      {(hideRespondButton && (ownerActionLabel || onEdit || onDelete)) && (
        <footer className="detail-cta-fixed">
          <div className="request-detail-owner-actions">
            {ownerActionLabel && onOwnerAction && (
              <button
                type="button"
                className="detail-action-btn detail-action-btn--primary"
                disabled={actionDisabled}
                onClick={handleOwnerActionClick}
              >
                {ownerActionLabel}
              </button>
            )}
            {onEdit && (
              <button
                type="button"
                className="detail-action-btn"
                disabled={actionDisabled}
                onClick={handleEditClick}
              >
                Редактировать
              </button>
            )}
            {onDelete && (
              <button
                type="button"
                className="detail-action-btn detail-action-btn--danger"
                disabled={actionDisabled}
                onClick={handleDeleteClick}
              >
                Удалить
              </button>
            )}
          </div>
        </footer>
      )}

      {!hideRespondButton && (
        <footer className="detail-cta-fixed">
          <button
            type="button"
            className={`detail-cta-btn ${responded ? 'detail-cta-btn--responded' : ''}`}
            disabled={responded}
            onClick={handleRespondClick}
          >
            {responded ? '✓ Вы уже откликнулись' : 'Откликнуться'}
          </button>
        </footer>
      )}
    </div>
  );
}
